import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { T, page, usePoll, Empty, Stat, MatchRow, Chip, SectionTitle, ScreenButton } from './ui'
import Scorecard from './Scorecard'

/**
 * One city's desk.
 *
 * Sports are split the way the venues are — the outdoor grounds and the office
 * floors are different buildings, staffed by different people — and each sport
 * offers the two displays that make sense for it: the order of play for the
 * whole hall, or one court on its own screen.
 */
const CityDesk = () => {
  const { city } = useParams()
  const [params, setParams] = useSearchParams()
  const date = params.get('date') || undefined
  const [openMatch, setOpenMatch] = useState(null)

  const { data, error } = usePoll(
    async () => {
      const [day, context] = await Promise.all([
        reportingApi.city({ city, date }),
        reportingApi.context({ city }),
      ])
      return { day, context }
    },
    20000,
    [city, date],
  )

  if (error) return <div style={page}><Empty>{error}</Empty></div>
  if (!data) return <div style={page}><Empty>Loading {city}…</Empty></div>

  const { day, context } = data
  const byKey = Object.fromEntries(day.sports.map((s) => [s.sport, s]))
  const settings = [['outdoor', 'Outdoor'], ['indoor', 'Indoor']]

  const liveNow = day.sports.flatMap((s) => s.live_matches || [])

  return (
    <div style={page}>
      <Link to="/board" style={back}>← All cities</Link>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        gap: 16, flexWrap: 'wrap', margin: '8px 0 18px',
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 26 }}>{city}</h2>
          <p style={{ margin: '4px 0 0', color: T.muted, fontSize: 13.5 }}>{day.day_label}</p>
        </div>
      </div>

      {/* Which competition day the desk is looking at. */}
      {day.days.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          {day.days.map((d) => (
            <Chip key={d.date} on={d.date === day.date}
                  onClick={() => setParams(d.date === day.date ? {} : { date: d.date })}>
              {d.label}{d.live > 0 ? ` · ${d.live} live` : ''}
            </Chip>
          ))}
        </div>
      )}

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
        gap: 12, marginBottom: 26,
      }}>
        <Stat value={day.totals.live} label="On court now" tone="live" />
        <Stat value={day.totals.played.toLocaleString('en-IN')} label="Played today" />
        <Stat value={day.totals.to_play.toLocaleString('en-IN')} label="Still to play" />
        <Stat value={day.totals.on_the_day.toLocaleString('en-IN')} label="Scheduled today" />
      </div>

      {liveNow.length > 0 && (
        <section style={{ marginBottom: 30 }}>
          <SectionTitle aside={`${liveNow.length} across ${city}`}>On court now</SectionTitle>
          <div style={{ display: 'grid', gap: 8 }}>
            {liveNow.map((m) => (
              <MatchRow key={m.id} m={m} onOpen={setOpenMatch}
                        action={<ScreenButton city={m.city} sport={m.sport} court={m.court} />} />
            ))}
          </div>
        </section>
      )}

      {settings.map(([setting, label]) => {
        const sports = context.sports.filter((s) => s.setting === setting)
        if (sports.length === 0) return null

        return (
          <section key={setting} style={{ marginBottom: 30 }}>
            <SectionTitle aside={sports[0]?.venue}>{label}</SectionTitle>

            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            }}>
              {sports.map((s) => (
                <SportCard key={s.sport} city={city} sport={s} today={byKey[s.sport]} />
              ))}
            </div>
          </section>
        )
      })}

      {openMatch && <Scorecard fixtureId={openMatch} onClose={() => setOpenMatch(null)} />}
    </div>
  )
}

const SportCard = ({ city, sport, today }) => {
  const live = today?.live ?? 0

  return (
    <div style={{
      background: T.card, borderRadius: 12, padding: '16px 18px',
      border: `1px solid ${live > 0 ? '#f0cdb4' : T.line}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 17 }}>{sport.label}</h3>
        {live > 0 && (
          <span style={{
            padding: '3px 10px', borderRadius: 20, background: T.liveBg, color: T.live,
            fontSize: 11.5, fontWeight: 800,
          }}>{live} live</span>
        )}
      </div>

      <p style={{ margin: '6px 0 0', fontSize: 12.5, color: T.muted }}>
        {sport.venue || 'Venue to be confirmed'}
      </p>
      <p style={{ margin: '2px 0 12px', fontSize: 12.5, color: T.faint, fontVariantNumeric: 'tabular-nums' }}>
        {sport.played.toLocaleString('en-IN')} of {sport.matches.toLocaleString('en-IN')} played
        {today ? ` · ${today.on_the_day} today` : ' · nothing today'}
      </p>

      <Link to={`/board/venue/${encodeURIComponent(city)}/${sport.sport}`} style={{
        display: 'block', textAlign: 'center', padding: '9px 14px', borderRadius: 8,
        background: T.accent, color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 13.5,
      }}>Order of play</Link>

      <div style={{ marginTop: 12 }}>
        <div style={{
          fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase',
          color: T.faint, fontWeight: 700, marginBottom: 6,
        }}>Screen for one {sport.unit_noun.toLowerCase()}</div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {sport.courts.map((c) => (
            <Link key={c.court}
                  to={`/board/screen/${encodeURIComponent(city)}/${sport.sport}/${encodeURIComponent(c.court)}`}
                  style={{
                    padding: '5px 11px', borderRadius: 7, fontSize: 12.5, textDecoration: 'none',
                    fontWeight: 600,
                    border: `1px solid ${c.live > 0 ? T.live : '#d9dee5'}`,
                    background: c.live > 0 ? T.liveBg : '#fff',
                    color: c.live > 0 ? T.live : T.muted,
                  }}>{c.court}</Link>
          ))}
          {sport.courts.length === 0 && <Empty>No courts assigned.</Empty>}
        </div>
      </div>
    </div>
  )
}

const back = { fontSize: 13, color: T.muted, textDecoration: 'none', fontWeight: 600 }

export default CityDesk
